import {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';

import * as errorPageTypes from '../../../../consts/app/errorPageTypes';
import * as routePaths from '../../../../consts/route/routePaths';
import useIsFwuEnabledForDevice from '../../../../hooks/fwu/useIsFwuEnabledForDevice';
import connectionService from '../../../../services/connectionService';
import deviceTypesService from '../../../../services/device/deviceTypesService';
import iccMarketService from '../../../../services/icc/iccMarketService';
import appRouterService from '../../../../services/route/appRouterService';
import {setNavigationTarget} from '../../../../state/ducks/global';
import {makeSelectIotDeviceMergedWithConsumerProduct} from '../../../../state/selectors/consumer';
import {selectAggregatedSettings} from '../../../../state/selectors/consumerAggregatedSettings';
import {selectNavigationTarget} from '../../../../state/selectors/global';
import {getState} from '../../../../state/store';
import useDeviceDiagnosticModeEnabled from '../../../DeviceSettingsPage/hooks/enabledFeatures/useDeviceDiagnosticModeEnabled';

const NAVIGATION_TARGETS = {
    SELECT_DEVICE: 'SELECT_DEVICE',
    PAIRING_GUIDE: 'PAIRING_GUIDE',
    AD_PHASE_2: 'AD_PHASE_2',
    FIRMWARE_CHECK: 'FIRMWARE_CHECK',
};

const useNavigationTargetRedirect = () => {
    const dispatch = useDispatch();
    const {pathname} = useLocation();
    const {target, deviceType, hasAlreadyBeenRedirectedToPairingPage} = useSelector(selectNavigationTarget) || {};
    const iotDevice = useSelector(makeSelectIotDeviceMergedWithConsumerProduct());

    const isFwuEnabledForDevice = useIsFwuEnabledForDevice();
    const {isAdvancedDeviceDiagnosticModeEnabled} = useDeviceDiagnosticModeEnabled(iotDevice);

    const clearNavigationTarget = () => dispatch(setNavigationTarget(undefined));
    const setHasAlreadyBeenRedirectedToPairingPage = () =>
        dispatch(
            setNavigationTarget({
                target,
                deviceType,
                hasAlreadyBeenRedirectedToPairingPage: true,
            })
        );

    useEffect(() => {
        if (!target) return;

        const isPrePairingPage = [routePaths.APP_INTRO, routePaths.HOME_PAGE, routePaths.MY_DEVICES].includes(pathname);
        const isPostPairingPage = [routePaths.DEVICE_SETTINGS].includes(pathname);
        const handlePrePairingTarget = async (redirect) => {
            if (!isPrePairingPage) return;

            getAggregatedSettingsIfNeeded();
            clearNavigationTarget();
            redirect();
        };
        const redirectToConnectionPage = () => {
            const productName = deviceTypesService.getProductNameByType(deviceType);

            connectionService.redirectToConnectionPage({type: deviceType, productName});
        };
        const handlePostPairingTarget = (redirect) => {
            if (isPrePairingPage && !hasAlreadyBeenRedirectedToPairingPage) {
                getAggregatedSettingsIfNeeded();
                setHasAlreadyBeenRedirectedToPairingPage();

                if (deviceType) {
                    redirectToConnectionPage();
                } else {
                    appRouterService.forwardToSelectDevice();
                }
            }

            if (isPostPairingPage) {
                getAggregatedSettingsIfNeeded();
                clearNavigationTarget();
                redirect();
            }
        };

        switch (target) {
            case NAVIGATION_TARGETS.SELECT_DEVICE: {
                handlePrePairingTarget(() => appRouterService.forwardToSelectDevice());
                return;
            }
            case NAVIGATION_TARGETS.PAIRING_GUIDE: {
                handlePrePairingTarget(() => {
                    redirectToConnectionPage();
                });

                return;
            }
            case NAVIGATION_TARGETS.AD_PHASE_2: {
                handlePostPairingTarget(() => {
                    if (!isAdvancedDeviceDiagnosticModeEnabled) return;

                    appRouterService.forwardToSecondPhaseAD();
                });

                return;
            }
            case NAVIGATION_TARGETS.FIRMWARE_CHECK: {
                handlePostPairingTarget(() => {
                    if (!isFwuEnabledForDevice) return;

                    appRouterService.forwardToUpdateCheckingPage();
                });

                return;
            }
            default:
                break;
        }
    }, [target, pathname, hasAlreadyBeenRedirectedToPairingPage]);
};

const getAggregatedSettingsIfNeeded = async () => {
    const aggregatedSettings = selectAggregatedSettings(getState());
    const isAggregatedSettings = !!aggregatedSettings;

    if (isAggregatedSettings) return;

    try {
        await iccMarketService.getConsumerAggregatedSettings();
    } catch (e) {
        appRouterService.forwardToErrorHandlingPage(errorPageTypes.ICC_TEMPORARY_ERROR);
    }
};

export default useNavigationTargetRedirect;
